import HeaderList from "../../components/HeaderList"
import { PageProps } from '../../components/types'
import DataGrid, { FormatterProps, RowsChangeData } from 'react-data-grid'
import { 
  PicklistStatus, 
  CheckMethod, 
  CheckMethodDesc, 
  OrderType, 
  MaterialType 
} from '../../constants/EnumType'
import { getColunmPicklist, PicklistCheckOrderColumn, PicklistCheckMaterialColumn } from '../../constants/Column'
import ButtonGroup from "../../components/ButtonGroup"
import SearchBox from "../../components/SearchBox"
import SelectOption from "../../components/SelectOption"
import { useCallback, useMemo, useState, useEffect } from "react";
import { groupBy as rowGrouper } from 'lodash';

import usePdCheckOrderPicklist from "../../hooks/usePdCheckOrderPicklist"
import usePicklistMovements from '../../hooks/usePicklistMovements'

import {
  checkItemMovement,
  uncheckItemMovement
} from '../../states/PicklistAction'
import { DgtPdOrderPicklist, DgtPdOrderPicklistMovement } from "../../models"
import RowClickable from '../../components/RowClickable'
import { HiDocumentText } from 'react-icons/hi'

const PicklistCheck = ({ title, division, line, from, to, username, readOnly }: PageProps) => {
  const [orderType, setOrderType] = useState<string>(line || '')
  const [statuses, setStatuses] = useState<string[]>([ PicklistStatus.WH_APPROVED ])
  const [dateRange, setDateRange] = useState<[Date, Date]>([from || new Date(), to || new Date()]);
  const [keyword, setKeyword] = useState<string>('')
  const [checkMethod, setCheckMethod] = useState<CheckMethod>(CheckMethod.BY_ORDER)
  const [div, setDiv] = useState<string>(division!)
  
  const [expandedGroupIds, setExpandedGroupIds] = useState<ReadonlySet<unknown>>(
    () => new Set<unknown>([''])
  );

  const filterChanged = useCallback((
    key: string, value: string, dateRange?: [Date, Date], values?: string[]) => {
    switch(key) {
      case 'Order Type':
        return setOrderType(value)
      case 'Status':
        return setStatuses(values!)
      case 'Released Date Range':
        return setDateRange(dateRange!)
      case 'Search':
        return setKeyword(value)
      case 'Division':
        return setDiv(value)
      default:
        break
    }
  }, [])

  const coupleStatuses = useMemo(() => 
    [PicklistStatus.WH_SHORTAGE, PicklistStatus.WH_APPROVED, PicklistStatus.PD_CHECKED]
  , [])

  useEffect(() => {
    if (checkMethod === CheckMethod.BY_ORDER) {
      setOrderType(line || '')
    } else if (checkMethod === CheckMethod.BY_MATERIAL) {
      setOrderType(line === OrderType.Mixing ? MaterialType.RAW : MaterialType.PKG)
    }
  }, [ checkMethod, line ])

  const picklists = usePdCheckOrderPicklist(statuses[0], orderType, dateRange[0], dateRange[1], keyword, div)

  const [ movements, mutateMovement ] = usePicklistMovements(
    statuses[0], orderType, dateRange[0], dateRange[1], keyword, div
  )

  useEffect(() => {
    if (movements.length > 0) {
      setExpandedGroupIds(new Set(movements.map(itm => itm.materialCode)))
    }
  }, [ movements ])

  const onRowsChange = async (rows: DgtPdOrderPicklistMovement[], 
    changedInfo: RowsChangeData<DgtPdOrderPicklistMovement>) => {
    const changedRowIdx = changedInfo.indexes[0]
    if (changedRowIdx < 0) return
    
    const changedRow = rows[changedRowIdx]
    if (changedRow.checkedDateTime! > 0) {
      const updated = await checkItemMovement(changedRow, username!)
      mutateMovement(updated)
    } else {
      const updated = await uncheckItemMovement(changedRow)
      mutateMovement(updated)
    }
  }

  let orderColumns = getColunmPicklist(PicklistCheckOrderColumn, {
    key: 'action', name: '',  width: 50, formatter: (props: FormatterProps<DgtPdOrderPicklist>) => {
      return <div className='flex items-center justify-center gap-1 text-blue-500'>
        <HiDocumentText /><RowClickable value={`${props.row.pk}${props.row.extraReturnId ? '/extra/'+props.row.extraReturnId:''}`} label='ดูใบเพิ่ม'/>
      </div> }
  })

  let materialColumns = Array.from(PicklistCheckMaterialColumn)
  if (readOnly) materialColumns.splice(-1)

  return <div className="page-container">
    <HeaderList title={title} subTitle={`แผนก : ${division}`}
      orderType={orderType}
      statuses={statuses}
      dateRange={dateRange}
      keywordSearch={keyword}
      coupleStatuses={coupleStatuses}
      filterChanged={filterChanged}
      searchable={false}
      filterType={false}
      isSpanFilterType={true}
      division={div}
    />
    <div className="mt-4 space-y-4 check-container">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        <SearchBox placeholder='Search...'
          className=" w-fit"
          value={keyword}
          onChanged={keyword => filterChanged('Search', keyword)}
          isRound={false}
        />
        <ButtonGroup
          className='flex w-full'
          menus={[CheckMethodDesc.BY_ORDER, CheckMethodDesc.BY_MATERIAL]}
          onChanged={setCheckMethod}
          selectedIndex={checkMethod}
          classNameTab={checkMethod === CheckMethod.BY_ORDER ? '':'text-rose-700 border-rose-700'}
          classNameSelectedBg={checkMethod === CheckMethod.BY_ORDER ? 'bg-blue-100' : 'bg-rose-100'}
        />
        <SelectOption title=''
          className=" w-fit justify-self-end"
          optionEnum={checkMethod === CheckMethod.BY_ORDER ? 
            Object.keys(OrderType).filter(typ => typ !== 'All') : Object.keys(MaterialType)}
          value={orderType} 
          onChanged={(val) => filterChanged('Order Type', val)}
        />
      </div>
      {checkMethod === CheckMethod.BY_ORDER ?
      <DataGrid style={{ height: "100%", contain: "none" }}
        columns={orderColumns}
        rows={picklists}
        defaultColumnOptions={{ resizable: true }}
        rowHeight={60}
      />
      :
      <DataGrid
        style={{ height: "100%", contain: "none" }}
        columns={materialColumns}
        rows={movements}
        rowKeyGetter={(row) => row.id}
        groupBy={['materialCode']}
        rowGrouper={rowGrouper}
        expandedGroupIds={expandedGroupIds}
        onExpandedGroupIdsChange={setExpandedGroupIds}
        defaultColumnOptions={{ resizable: true }}
        onRowsChange={onRowsChange}
      />
      }
    </div>
  </div>
}

export default PicklistCheck